import { readdir, readFile } from "node:fs/promises";
import { join } from "node:path";
import type { LintFinding, LintReport, LintRule } from "./types.js";

export interface LintOptions {
  /** Directory to scan recursively. */
  readonly dir: string;
  /** Rules to apply to every file. */
  readonly rules: readonly LintRule[];
  /** File extensions to include. Defaults to `[".md"]`. */
  readonly extensions?: readonly string[];
}

/**
 * Recursively scan `dir` for files with the configured extensions and run
 * every rule against each file. Findings from all rules are aggregated into
 * a single `LintReport`. Order of findings within the report is not
 * guaranteed beyond "file by file, rule by rule".
 */
export async function lintDirectory(options: LintOptions): Promise<LintReport> {
  const start = Date.now();
  const extensions = options.extensions ?? [".md"];
  const files = await collectFiles(options.dir, extensions);
  const findings: LintFinding[] = [];

  for (const file of files) {
    const content = await readFile(file, "utf8");
    for (const rule of options.rules) {
      const result = await rule.check({ file, content });
      for (const f of result) findings.push(f);
    }
  }

  return {
    findings,
    filesScanned: files.length,
    rulesRun: options.rules.length,
    durationMs: Date.now() - start,
  };
}

// Directories never worth linting: third-party deps and operational/VCS state. A project PoC
// committed under data/ can carry a whole node_modules tree, and .vortex holds framework backups
// (e.g. backups of CLAUDE.md, which legitimately have no `type:` frontmatter) — scanning either
// floods the report with errors for files that are not the user's records.
const IGNORE_DIRS = new Set(["node_modules", ".git", ".vortex"]);

async function collectFiles(
  dir: string,
  extensions: readonly string[],
): Promise<string[]> {
  const out: string[] = [];
  const stack: string[] = [dir];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) break;
    let entries;
    try {
      entries = await readdir(current, { withFileTypes: true });
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") continue;
      throw e;
    }
    for (const entry of entries) {
      const full = join(current, entry.name);
      if (entry.isDirectory()) {
        if (IGNORE_DIRS.has(entry.name)) continue;
        stack.push(full);
      } else if (
        entry.isFile() &&
        extensions.some((ext) => entry.name.endsWith(ext))
      ) {
        out.push(full);
      }
    }
  }
  return out.sort();
}
